import React, { useState, useEffect, useRef } from "react";
import { FAQTabs } from "../FAQTabs/FAQTabs";
import { TextField } from "@arctheme-components/elements/TextField/TextField";
import { Headings } from "@arctheme-components/elements/Headings/Headings";
import { Paragraph } from "@project-stories/elements/Paragraph/Paragraph";
import { Button } from "@project-stories/elements/Button/Button";
import { Accordion } from "@arctheme-components/elements/Accordion/Accordion";
import { Image } from "@arctheme-components/elements/Image/Image";
import { Collapsible } from "@project-stories/elements/Collapsible/Collapsible";
import { Media } from "@arctheme-components/elements/Media/Media";
import { handleize } from "frontend/js/helpers";

const TwoColumnLayout = ({ blocks, settings, isMobile }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [visibleCount, setVisibleCount] = useState(settings.load_more_limit);
  const [currentHash, setCurrentHash] = useState(window.location.hash);
  const faqRefs = useRef({});

  const shouldDisplay = () => {
    if (settings.display_mode === "desktop-only" && isMobile) return false;
    if (settings.display_mode === "mobile-only" && !isMobile) return false;
    return true;
  };

  let borderRadius = settings.rounded_corners;
  if (isMobile && !Number.isNaN(settings.rounded_corners_mobile)) {
    borderRadius = settings.rounded_corners_mobile;
  }

  const getAccordionItemClass = () => {
    return `${settings.vertical_spacing ? `mb-[${settings.vertical_spacing}px]` : 'mb-0'} ${borderRadius ? `rounded-[${borderRadius}px]` : ''}`;
  };

  const faqItems = blocks
    .filter((block) => block.type === "column_block")
    .map((faqItem) => ({
      title: faqItem.settings.title,
      content: faqItem.settings.content,
      image: faqItem.settings.image,
      video: faqItem.settings.video,
      category: faqItem.settings.category,
      filter_tag: faqItem.settings.filter_tag,
      anchor_id: faqItem.settings?.anchor_id || (faqItem.settings.title && handleize(faqItem.settings?.title)),
      column: faqItem.settings.column,
    }));

  const imageBlock = blocks.find((block) => block.type === "column_image");
  const ctaBlock = blocks.find((block) => block.type === "column_CTA");

  const filteredFaqItems = faqItems.filter((item) => {
    const matchesSearch = item.title
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesCategory =
      activeTab === "All" || item.filter_tag === activeTab;
    return matchesSearch && matchesCategory;
  });

  useEffect(() => {
    function scrollToAnchor() {
      if (currentHash) {
        const anchor = currentHash.replace('#', '');
        setTimeout(() => {
          const el = document.getElementById(anchor);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 100);
      }
    }
    scrollToAnchor();
  }, [filteredFaqItems, currentHash]);

  useEffect(() => {
    const onHashChange = () => setCurrentHash(window.location.hash);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const uniqueFilterTags = [
    ...new Set(faqItems.map((item) => item.filter_tag)),
  ];

  const tabs = [
    { label: "All FAQ's", value: "All" },
    ...uniqueFilterTags.map((tag) => ({
      label: tag,
      value: tag,
    })),
  ];

  const contentFont = isMobile
    ? settings.content_font_mobile
    : settings.content_font;

  const renderFAQ = () => (
    <>
      {(settings.show_filter_bar || settings.show_search_bar) && (
        <div className="faq__container-filters w-full">
          {settings.show_filter_bar && (
            <FAQTabs
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              settings={settings}
            />
          )}
          {settings.show_search_bar && (
            <div className="py-[16px]">
              <TextField
                placeholder="Search our FAQ's"
                icon="search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
              />
            </div>
          )}
        </div>
      )}
      <div className="faq__container-accordion w-full flex">
        <div className="faq__container-accordion-left">
          {filteredFaqItems.map((item, index) => (
            <Collapsible
              key={`faq_${index}`}
              id={item.anchor_id}
              ref={el => { if (el) faqRefs.current[item.anchor_id] = el; }}
              title={item.title}
              active={currentHash.replace('#', '') === item.anchor_id}
              titleClass={`${settings.title_font || 'bfb'} min-h-[72px]`}
              contentClass={`${settings.content_font || 'b2'}`}
              openIcon={settings.accordion_icon_open || "icon-caret-down-thin"}
              closeIcon={settings.accordion_icon_close || "icon-caret-up-thin"}
              className={getAccordionItemClass()}
              background={settings.accordion_background || "transparent"}
              readMore={settings.show_read_more}
            >
              {(item.video || item.image) && (
                <div className={"accordion__content-column"}>
                  <Media
                    videoItem={item.video}
                    imageItem={item.image}
                    index={index}
                  />
                </div>
              )}
              {item.content && (
                <div className={"accordion__content-column"}>
                  <Paragraph
                    text={item.content}
                    type={contentFont || 'b2'}
                    shouldTruncateText={settings.show_read_more}
                    maxChars={300}
                    classes="rich-content"
                  />
                </div>
              )}
            </Collapsible>
          ))}
        </div>
      </div>
      {settings.use_load_more && visibleCount < filteredFaqItems.length && (
        <div className="faq__container-button-container relative">
          <div className="faq__container-button">
            <Button
              label="Load More"
              style="secondary"
              onClick={() => setVisibleCount(visibleCount + 10)}
            />
          </div>
        </div>
      )}
    </>
  );

  if (!shouldDisplay()) return null;

  let maxWidth = settings.width === 0 ? '100%' : settings.width;
  if (isMobile && !Number.isNaN(settings.width_mobile)) {
    maxWidth = settings.width_mobile === 0 ? '100%' : settings.width_mobile;
  }

  const showSubtext = isMobile
    ? settings.show_subtext_on_mobile
    : settings.show_subtext_on_desktop;

  return (
    <div className="faq">
      <div
        className={`flex flex-row lg:gap-20 items-${settings.content_alignment_mobile} lg:items-${settings.content_alignment_desktop} justify-${settings.content_position_desktop} mx-auto`}
        style={{ maxWidth }}
      >
        <div className={`faq-details flex flex-col gap-8 basis-1/2`}>
          <div className={`faq-top-section flex flex-col space-y-[12px] lg:space-y-[16px]`}>
            <div>
              <Paragraph
                text={settings.title}
                type={`${isMobile ? settings.heading_type_mobile : settings.heading_type} faq-heading`}
              />
              {showSubtext &&
                <div
                  className="faq__container-subcopy mt-[7px]"
                  style={{ textAlign: "left" }}
                >
                  <Paragraph
                    text={settings.subtext}
                    type={
                      isMobile
                        ? settings.subheading_type_mobile
                        : settings.subheading_type
                    }
                  />
                </div>
              }
            </div>
            {ctaBlock && ctaBlock.settings.column == 1 && (
              <Button
                label={ctaBlock.settings.cta_text}
                style={`${ctaBlock.settings.cta_style}`}
                link={`${ctaBlock.settings.cta_url}`}
              />
            )}
          </div>
          {imageBlock && imageBlock.settings.column == 1 && (
            <Image imageUrl={imageBlock.settings.image} alt={"Image"} width="100%" />
          )}
          {settings.faq_to_left && renderFAQ()}
        </div>
        <div className={`faq-data basis-1/2 relative`}>
          {imageBlock && imageBlock.settings.column == 2 && (
            <Image imageUrl={imageBlock.settings.image} alt={"Image"} width="100%" />
          )}
          {ctaBlock && ctaBlock.settings.column == 2 && (
            <Button
              label={ctaBlock.settings.cta_text}
              style={`${ctaBlock.settings.cta_style}`}
              link={`${ctaBlock.settings.cta_url}`}
            />
          )}
          {!settings.faq_to_left && renderFAQ()}
        </div>
      </div>
    </div>
  );
};

export default TwoColumnLayout;
